import { useGetMeetingStatusList } from 'lib/api/meetings/useGetMeetingStatusList';
import {
  MeetingResponse,
  MEETING_STATUSES,
  MeetingStatus,
} from 'lib/api/meetings/types';
import { LoadingIndicator, Search, Tabs } from 'lib/components';
import { Container, MainWrapper } from 'lib/components/styles/layout';
import { useAuth } from 'lib/context';
import placeholder from 'assets/images/table/placeholderBig.webp';
import { theme } from 'lib/style';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { Tab } from 'lib/components/tabs';
import { MeetingListItem } from '../components/MeetingListItem';
import { MeetingListActions } from '../components/meetingListActions/MeetingListActions';
import { CancelMeetingModal } from '../components/modals/CancelMeetingModal';
import { useStartNowMeetingMutation } from 'lib/api/meetings/useStartNowMeetingMutation';
import { DeleteMeetingModal } from '../components/modals/DeleteMeetingModal';
import { useSendInviteMeetingMutation } from 'lib/api/meetings/useSendInviteMeetingMutation';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
`;
const SearchBar = styled.div`
  margin-right: 12px;
  min-width: 400px;
`;

const HeaderTitle = styled.div`
  font-style: normal;
  font-weight: 800;
  font-size: 24px;
  line-height: 40px;
  color: ${theme.palette.gray100};
  padding-top: 16px;
`;

const NoLivesLayout = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 450px;
  background-image: url(${placeholder});
  background-repeat: no-repeat;
  background-size: cover;
`;
const Text = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: ${theme.palette.themeDark};
  text-align: center;
  margin-bottom: 32px;
`;

const MainDiv = styled.div`
  margin-top: 40px;
`;

const TabsContainer = styled.div`
  width: 492px;
`;

export enum TAB_IDS {
  SCHEDULED = 0,
  ENDED = 1,
  CANCELED = 2,
  RECURRING = 3,
}

const getTabs = (t: (key: string) => string): Tab[] => [
  {
    id: TAB_IDS.SCHEDULED,
    title: t('Scheduled'),
    component: <></>,
    inActiveTextColor: theme.palette.gray60,
  },
  {
    id: TAB_IDS.ENDED,
    title: t('Ended'),
    component: <></>,
    inActiveTextColor: theme.palette.gray60,
  },
  {
    id: TAB_IDS.CANCELED,
    title: t('Canceled'),
    component: <></>,
    inActiveTextColor: theme.palette.gray60,
  },
  {
    id: TAB_IDS.RECURRING,
    title: t('Recurring'),
    component: <></>,
    inActiveTextColor: theme.palette.gray60,
  },
];

const getTabStatus = (activeTab: number) => {
  switch (activeTab) {
    case 0:
      return MEETING_STATUSES.SCHEDULED;
    case 1:
      return MEETING_STATUSES.FINISHED;
    case 2:
      return MEETING_STATUSES.CANCELED;
    case 3:
      return MEETING_STATUSES.RECURRING;
    default:
      return MEETING_STATUSES.SCHEDULED;
  }
};

export const MeetingList = () => {
  const { t } = useTranslation();
  const [statusType, setStatusType] = useState<MeetingStatus>(
    MEETING_STATUSES.SCHEDULED
  );
  const {
    data: meetingListStatusData,
    isLoading,
    refetch,
  } = useGetMeetingStatusList(statusType);
  const { userData } = useAuth();
  const history = useHistory();

  const { mutateAsync: sendInvite, isLoading: emailSending } =
    useSendInviteMeetingMutation();
  const { mutateAsync: startNowAsync } = useStartNowMeetingMutation();
  const [meetings, setMeetings] = useState<MeetingResponse[]>([]);
  const [selectedMeeting, setSelectedMeeting] = useState<MeetingResponse>();

  const [showModals, setShowModals] = useState<'delete' | 'cancel' | null>(
    null
  );
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(TAB_IDS.SCHEDULED);

  useEffect(() => {
    const tabStatus = getTabStatus(activeTab);
    setStatusType(tabStatus);
  }, [activeTab]);

  useEffect(() => {
    refetch();
  }, [statusType]);

  useEffect(() => {
    if (meetingListStatusData) {
      setMeetings(meetingListStatusData);
    }
  }, [meetingListStatusData]);

  const onSearch = (searchTerm: string) => {
    setSearch(searchTerm);
    if (!searchTerm) {
      meetingListStatusData && setMeetings(meetingListStatusData);
      return;
    }

    const filteredLives = meetings.filter(live => {
      const { meetingId, attendees, title } = live;
      const emails = attendees
        .map(attendee => attendee.email)
        .filter(Boolean)
        .join('|')
        .toLowerCase();

      return (
        meetingId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emails?.includes(searchTerm.toLowerCase())
      );
    });

    setMeetings(filteredLives);
  };

  const onStartNowClick = async (meetingId: string | null) => {
    startNowAsync({
      meetingId,
      userId: Number(userData.id),
    });
  };

  const onSendInvite = async (meetingId: string) => {
    if (meetingId) {
      await sendInvite(meetingId);
    }
  };

  const onMeetingCancel = (meeting: MeetingResponse) => {
    setSelectedMeeting(meeting);
    setShowModals('cancel');
  };

  const onMeetingDelete = (meeting: MeetingResponse) => {
    setSelectedMeeting(meeting);
    setShowModals('delete');
  };

  const goToMeeting = (
    meetingId: string | null,
    meetingStatus: string | null
  ) => {
    if (meetingId) {
      history.push(`/meetings/${meetingId}`);
      return;
    }

    const qs = new URLSearchParams();
    if (meetingStatus !== null && meetingStatus !== '') {
      qs.set('status', meetingStatus);
    }

    history.push({
      pathname: '/meetings/new',
      search: qs.toString() ? `?${qs.toString()}` : undefined,
    });
  };

  if (isLoading) {
    return <LoadingIndicator isLoading={isLoading} />;
  }

  return (
    <Container>
      <MainWrapper resetPadding>
        <HeaderTitle>{t('Meetings')}</HeaderTitle>
        <TabsContainer>
          <Tabs
            tabs={getTabs(t)}
            defaultActiveTab={activeTab}
            onChange={setActiveTab}
            navbarPadding='16px 0px 0px 0px'
          />
        </TabsContainer>
        <HeaderContainer>
          <SearchBar>
            <Search
              key={activeTab}
              placeholder={t('Search by name, ID, or participants…')}
              onSearch={onSearch}
            />
          </SearchBar>
          <MeetingListActions
            handleOpenLiveDetailsModal={goToMeeting}
            activeTab={activeTab}
          />
        </HeaderContainer>
        {meetings.length !== 0 && (
          <MainDiv>
            {meetings.map(element => (
              <MeetingListItem
                data={element}
                key={element.meetingId}
                startNow={onStartNowClick}
                edit={() =>
                  goToMeeting(element.meetingId, element.meetingStatus)
                }
                sendInvite={onSendInvite}
                emailSending={emailSending}
                onCancel={onMeetingCancel}
                onDelete={onMeetingDelete}
                activeTab={activeTab}
              />
            ))}
          </MainDiv>
        )}
        {meetings.length === 0 && (
          <NoLivesLayout>
            <Text>
              {!search
                ? t('No {{statusType}} meetings', { statusType })
                : t('No matching results found')}
            </Text>
          </NoLivesLayout>
        )}
        {showModals === 'cancel' && selectedMeeting?.meetingId && (
          <CancelMeetingModal
            attendees={selectedMeeting?.attendees || []}
            meetingId={selectedMeeting.meetingId}
            handleModalClose={() => setShowModals(null)}
            handleCloseAllModals={() => setShowModals(null)}
          />
        )}

        {showModals === 'delete' && selectedMeeting?.meetingId && (
          <DeleteMeetingModal
            meetingId={selectedMeeting.meetingId}
            handleModalClose={() => setShowModals(null)}
            handleCloseAllModals={() => setShowModals(null)}
          />
        )}
      </MainWrapper>
    </Container>
  );
};
